import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  LinearProgress,
  Alert,
  Snackbar,
  Tab,
  Tabs,
  Stack,
  Divider,
  Avatar,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Storage as StorageIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Storage as DatabaseIcon, // Changed from Database
  Api as ApiIcon,
  FileUpload as FileIcon,
  Link as LinkIcon,
  Sync as SyncIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

function DataSources({ darkMode }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [dataSources, setDataSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const [newSource, setNewSource] = useState({
    name: '',
    type: 'file',
    description: '',
    config: {},
    enabled: true,
  });

  const sourceTypes = [
    {
      value: 'file',
      label: 'File Upload',
      icon: <FileIcon />,
      description: 'Upload documents, PDFs, images, or text files',
    },
    {
      value: 'database',
      label: 'Database',
      icon: <DatabaseIcon />, // Now using Storage icon for database
      description: 'Connect to SQL or NoSQL databases',
    },
    {
      value: 'api',
      label: 'REST API',
      icon: <ApiIcon />,
      description: 'Pull data from external APIs',
    },
    {
      value: 'webhook',
      label: 'Webhook',
      icon: <LinkIcon />,
      description: 'Receive data via webhook endpoints',
    },
    {
      value: 'cloud',
      label: 'Cloud Storage',
      icon: <UploadIcon />,
      description: 'Connect to Google Drive, Dropbox, S3, etc.',
    },
  ];

  useEffect(() => {
    loadDataSources();
  }, []);

  const loadDataSources = async () => {
    setLoading(true);
    try {
      // Mock data - replace with actual API call
      const mockSources = [
        {
          id: '1',
          name: 'Company Knowledge Base',
          type: 'file',
          status: 'active',
          lastSync: new Date().toISOString(),
          recordCount: 1250,
          size: '45.2 MB',
          description: 'Internal company documentation and policies',
          config: { format: 'pdf', autoSync: true },
          enabled: true,
          createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: '2',
          name: 'Customer Database',
          type: 'database',
          status: 'syncing',
          lastSync: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          recordCount: 8500,
          size: '12.8 MB',
          description: 'Customer information and support tickets',
          config: { host: 'localhost:5432', database: 'customers' },
          enabled: true,
          createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        },
        {
          id: '3',
          name: 'Product API',
          type: 'api',
          status: 'error',
          lastSync: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          recordCount: 0,
          size: '0 MB',
          description: 'Product catalog and inventory data',
          config: { endpoint: 'https://api.company.com/products', method: 'GET' },
          enabled: false,
          createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        },
      ];

      setDataSources(mockSources);
    } catch (error) {
      console.error('Error loading data sources:', error);
      setSnackbar({ open: true, message: 'Error loading data sources', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'syncing': return 'info';
      case 'error': return 'error';
      case 'warning': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckIcon color="success" />;
      case 'syncing': return <SyncIcon color="info" />;
      case 'error': return <ErrorIcon color="error" />;
      case 'warning': return <WarningIcon color="warning" />;
      default: return null;
    }
  };

  const handleAddSource = () => {
    // Mock adding source - replace with actual API call
    const source = {
      id: Date.now().toString(),
      ...newSource,
      status: 'active',
      lastSync: new Date().toISOString(),
      recordCount: 0,
      size: '0 MB',
      createdAt: new Date().toISOString(),
    };

    setDataSources([...dataSources, source]);
    setAddDialogOpen(false);
    setNewSource({
      name: '', type: 'file', description: '', config: {}, enabled: true,
    });
    setSnackbar({ open: true, message: 'Data source added successfully!', severity: 'success' });
  };

  const handleEditSource = () => {
    setDataSources(dataSources.map((source) => (source.id === selectedSource.id ? selectedSource : source)));
    setEditDialogOpen(false);
    setSelectedSource(null);
    setSnackbar({ open: true, message: 'Data source updated successfully!', severity: 'success' });
  };

  const handleDeleteSource = (id) => {
    setDataSources(dataSources.filter((source) => source.id !== id));
    setSnackbar({ open: true, message: 'Data source deleted', severity: 'info' });
  };

  const handleSyncSource = (id) => {
    setDataSources(dataSources.map((source) => (source.id === id ? { ...source, status: 'syncing', lastSync: new Date().toISOString() } : source)));
    setSnackbar({ open: true, message: 'Sync started', severity: 'info' });

    // Simulate sync completion
    setTimeout(() => {
      setDataSources((prev) => prev.map((source) => (source.id === id ? { ...source, status: 'active' } : source)));
    }, 3000);
  };

  const handleToggleSource = (id) => {
    setDataSources(dataSources.map((source) => (source.id === id ? { ...source, enabled: !source.enabled } : source)));
  };

  const openEditDialog = (source) => {
    setSelectedSource({ ...source });
    setEditDialogOpen(true);
  };

  const renderConfigForm = (type, config, setConfig) => {
    switch (type) {
      case 'file':
        return (
          <Box>
            <input
              type="file"
              multiple
              style={{ marginBottom: 16 }}
              onChange={(e) => setConfig({ ...config, files: e.target.files })}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={config.autoSync || false}
                  onChange={(e) => setConfig({ ...config, autoSync: e.target.checked })}
                />
              )}
              label="Auto-sync new files"
            />
          </Box>
        );

      case 'database':
        return (
          <Stack spacing={2}>
            <TextField
              label="Host"
              value={config.host || ''}
              onChange={(e) => setConfig({ ...config, host: e.target.value })}
              placeholder="localhost:5432"
              fullWidth
            />
            <TextField
              label="Database Name"
              value={config.database || ''}
              onChange={(e) => setConfig({ ...config, database: e.target.value })}
              fullWidth
            />
            <TextField
              label="Username"
              value={config.username || ''}
              onChange={(e) => setConfig({ ...config, username: e.target.value })}
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={config.password || ''}
              onChange={(e) => setConfig({ ...config, password: e.target.value })}
              fullWidth
            />
          </Stack>
        );

      case 'api':
        return (
          <Stack spacing={2}>
            <TextField
              label="API Endpoint"
              value={config.endpoint || ''}
              onChange={(e) => setConfig({ ...config, endpoint: e.target.value })}
              placeholder="https://api.example.com/data"
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Method</InputLabel>
              <Select
                value={config.method || 'GET'}
                onChange={(e) => setConfig({ ...config, method: e.target.value })}
                label="Method"
              >
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="API Key"
              type="password"
              value={config.apiKey || ''}
              onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
              fullWidth
            />
          </Stack>
        );

      case 'webhook':
        return (
          <Stack spacing={2}>
            <Alert severity="info">
              Webhook URL: https://your-domain.com/webhook/
              {Date.now()}
            </Alert>
            <TextField
              label="Secret Key"
              value={config.secret || ''}
              onChange={(e) => setConfig({ ...config, secret: e.target.value })}
              fullWidth
              helperText="Optional secret for webhook verification"
            />
          </Stack>
        );

      case 'cloud':
        return (
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Provider</InputLabel>
              <Select
                value={config.provider || 'gdrive'}
                onChange={(e) => setConfig({ ...config, provider: e.target.value })}
                label="Provider"
              >
                <MenuItem value="gdrive">Google Drive</MenuItem>
                <MenuItem value="dropbox">Dropbox</MenuItem>
                <MenuItem value="s3">Amazon S3</MenuItem>
                <MenuItem value="onedrive">OneDrive</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Folder Path"
              value={config.folderPath || ''}
              onChange={(e) => setConfig({ ...config, folderPath: e.target.value })}
              placeholder="/shared/documents"
              fullWidth
            />
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4,
      }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, mb: 1,
            }}
          >
            <StorageIcon />
            {' '}
            Data Sources
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your data connections and knowledge sources.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadDataSources}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Source
          </Button>
        </Stack>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <StorageIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4">{dataSources.length}</Typography>
              <Typography color="text.secondary">Total Sources</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4">
                {dataSources.filter((s) => s.status === 'active').length}
              </Typography>
              <Typography color="text.secondary">Active Sources</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <DatabaseIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4">
                {dataSources.reduce((sum, s) => sum + s.recordCount, 0).toLocaleString()}
              </Typography>
              <Typography color="text.secondary">Total Records</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <SyncIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4">
                {dataSources.filter((s) => s.status === 'syncing').length}
              </Typography>
              <Typography color="text.secondary">Syncing</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="fullWidth"
        >
          <Tab label="All Sources" />
          <Tab label="Files" />
          <Tab label="Databases" />
          <Tab label="APIs" />
          <Tab label="Webhooks" />
        </Tabs>
      </Paper>

      {/* Data Sources List */}
      <Grid container spacing={3}>
        {dataSources
          .filter((source) => {
            if (selectedTab === 0) return true;
            const typeFilters = ['file', 'database', 'api', 'webhook'];
            return source.type === typeFilters[selectedTab - 1];
          })
          .map((source) => (
            <Grid item xs={12} md={6} lg={4} key={source.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      {sourceTypes.find((t) => t.value === source.type)?.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6">{source.name}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={source.type}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={source.status}
                          size="small"
                          color={getStatusColor(source.status)}
                          icon={getStatusIcon(source.status)}
                        />
                      </Box>
                    </Box>
                    <Switch
                      checked={source.enabled}
                      onChange={() => handleToggleSource(source.id)}
                      color="primary"
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {source.description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Records</Typography>
                      <Typography variant="body2">{source.recordCount.toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Size</Typography>
                      <Typography variant="body2">{source.size}</Typography>
                    </Grid>
                  </Grid>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon fontSize="small" />
                    Last sync:
                    {' '}
                    {new Date(source.lastSync).toLocaleDateString()}
                  </Typography>
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<SyncIcon />}
                    onClick={() => handleSyncSource(source.id)}
                    disabled={source.status === 'syncing' || !source.enabled}
                  >
                    Sync
                  </Button>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => openEditDialog(source)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    color="info"
                  >
                    View
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteSource(source.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
      </Grid>

      {loading && <LinearProgress sx={{ mt: 2 }} />}

      {/* Add Source Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Data Source</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Source Name"
              value={newSource.name}
              onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
              fullWidth
              required
            />

            <FormControl fullWidth>
              <InputLabel>Source Type</InputLabel>
              <Select
                value={newSource.type}
                onChange={(e) => setNewSource({ ...newSource, type: e.target.value, config: {} })}
                label="Source Type"
              >
                {sourceTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {type.icon}
                      <Box>
                        <Typography>{type.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {type.description}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Description"
              value={newSource.description}
              onChange={(e) => setNewSource({ ...newSource, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            {renderConfigForm(newSource.type, newSource.config, (config) => setNewSource({ ...newSource, config }))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddSource} variant="contained">Add Source</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Source Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Data Source</DialogTitle>
        <DialogContent>
          {selectedSource && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label="Source Name"
                value={selectedSource.name}
                onChange={(e) => setSelectedSource({ ...selectedSource, name: e.target.value })}
                fullWidth
              />

              <TextField
                label="Description"
                value={selectedSource.description}
                onChange={(e) => setSelectedSource({ ...selectedSource, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />

              {renderConfigForm(selectedSource.type, selectedSource.config, (config) => setSelectedSource({ ...selectedSource, config }))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSource} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DataSources;
